import { Player } from "@videotranslator/player";
import "@videotranslator/player/css";
import { CircularProgress, Typography } from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import EventBus from "orion-components/SharedComponents/EventBus";
import StreamFailed from "../Wowza/utils/StreamFailed";
import { cameraService } from "client-app-core";
import { getTranslation } from "orion-components/i18n/Actions";

// Refresh the token this many ms before it expires
const TOKEN_REFRESH_BUFFER_MS = 30_000;

const createEclipseAuthProvider = cameraId => {
	let cachedSession = null;

	const fetchSession = refreshToken =>
		new Promise((resolve, reject) => {
			const body = refreshToken ? { refresh_token: refreshToken } : {};
			cameraService.getPlayerSession(cameraId, body, (err, result) => {
				if (err) reject(err);
				else resolve(result);
			});
		});

	const isNearExpiry = expiresAt => {
		return Date.now() >= new Date(expiresAt).getTime() - TOKEN_REFRESH_BUFFER_MS;
	};

	return {
		authType: "jwt",
		async getCredential() {
			if (!cachedSession || isNearExpiry(cachedSession.expires_at)) {
				cachedSession = await fetchSession(cachedSession?.refresh_token);
			}
			return cachedSession.access_token;
		}
	};
};

// Standard playback speeds supported by VideoTranslator player
const STANDARD_SPEED_RANGE = [-16, -8, -4, -2, -1, -0.5, 0.5, 1, 2, 4, 8, 16];

// Errors that have no impact on playback, e.g. in case of Cancel failed
// it's part of a retry loop that recovers without user intervention
const SUPPRESS_ERRORS = ["Cancel failed: 400"];

const VideoTranslatorPlayer = ({ instanceId, player, camera, streamUrl, selectedVideoMode }) => {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);
	const [eclipseStreamId, setEclipseStreamId] = useState(null);
	const playerDiv = useRef();
	const playerRef = useRef();
	const selectedVideoModeRef = useRef(selectedVideoMode);
	const previousSelectedVideoModeRef = useRef(selectedVideoMode);
	const cursorRef = useRef({});
	const playerReadyRef = useRef(false);
	const pendingCommandsRef = useRef([]);
	const currentFrameRef = useRef(null);
	const frameAnchorInvalidRef = useRef(true);
	const authProviderRef = useRef(null);
	const authProviderCameraIdRef = useRef(null);
	const pendingLiveMode = useRef(false);
	// When the player starts in recorded mode the ILB/stream is never set up for live,
	// so setMode("live") fails. Track whether we need a full remount to go live.
	const needsFullReinitForLive = useRef(selectedVideoMode === "recorded");
	const [remountKey, setRemountKey] = useState(0);

	if (camera.player?.backend === "eclipse") {
		if (authProviderCameraIdRef.current !== camera.id) {
			authProviderRef.current = createEclipseAuthProvider(camera.id);
			authProviderCameraIdRef.current = camera.id;
		}
	} else {
		authProviderRef.current = null;
		authProviderCameraIdRef.current = null;
	}

	useEffect(() => {
		selectedVideoModeRef.current = selectedVideoMode;
	}, [selectedVideoMode]);

	useEffect(() => {
		if (camera.player?.backend !== "eclipse") {
			setEclipseStreamId(null);
			return;
		}
		let cancelled = false;
		(async () => {
			try {
				const token = await authProviderRef.current.getCredential();
				// build URL for Eclipse API using cameraId/deviceId as job_uuid, which should return the active stream for the camera if it exists
				const resp = await fetch(
					`${camera.player.eclipseApi}/streams?job_uuid=${camera.connection.cameraId || camera.connection.deviceId}`,
					{
						headers: { Authorization: `Bearer ${token}` }
					}
				);
				if (!resp.ok) throw new Error(`Eclipse streams fetch failed: ${resp.status}`);
				const data = await resp.json();
				const stream = data.streams?.[0];
				if (!cancelled) setEclipseStreamId(stream?.mediamtx_path ?? null);
			} catch (err) {
				console.error("Eclipse stream lookup failed:", err);
				if (!cancelled) setEclipseStreamId(null);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [camera.connection.cameraId, camera.connection.deviceId, camera.player?.backend]);

	useEffect(() => {
		EventBus.subscribe("Universal_PlaybackCommand", execPlaybackCommand);
		return () => {
			EventBus.unsubscribe("Universal_PlaybackCommand", execPlaybackCommand);
		};
	}, []);

	const publishCurrentRecordedTimestamp = () => {
		const currentTime = playerRef.current.getCurrentTime();
		const { startTime } = cursorRef.current || {};
		if (!startTime || currentTime === undefined) return;

		const timestamp = new Date(startTime.getTime() + currentTime * 1000);
		EventBus.publish("setCurrentRecordedTimestamp", { instanceId, time: timestamp });
	};

	const execPlaybackCommand = async cmd => {
		if (!playerReadyRef.current || !playerRef.current) {
			pendingCommandsRef.current.push(cmd);
			return;
		}
		// ignore if live
		if (selectedVideoModeRef.current === "live") {
			return;
		} else {
			const { instanceId: cmdInstanceId, command } = cmd;
			if (instanceId !== cmdInstanceId) return;
			switch (command) {
				// handle commands here
				case "play":
					// While playing, frames advance continuously; invalidate manual frame anchor.
					currentFrameRef.current = null;
					frameAnchorInvalidRef.current = true;
					playerRef.current.play();
					break;
				case "pause":
					playerRef.current.pause();
					// Do not trust getCurrentFrame() here; it may still reflect stale UI state.
					currentFrameRef.current = null;
					frameAnchorInvalidRef.current = true;
					break;
				case "setCursor":
					if (cmd.seeking) {
						const { time } = cmd;
						const { startTime } = cursorRef.current || {};
						if (!startTime) return;
						const newTimestamp = new Date(time);
						const secondsFromStart = (newTimestamp.getTime() - startTime.getTime()) / 1000;
						if (secondsFromStart > cursorRef.current.loadedDuration) return;
						// Reset frame tracking when scrubbing to a new position
						currentFrameRef.current = null;
						frameAnchorInvalidRef.current = true;
						playerRef.current.seek(secondsFromStart);
					} else {
						const startTime = new Date(cmd.time);
						const endTime = new Date(startTime.getTime() + cmd.duration * 1000);
						cursorRef.current = {
							startTime,
							endTime,
							duration: cmd.duration,
							loop: !cmd.disableAutoLoop
						};
						// Reset frame tracking when loading a new time range
						currentFrameRef.current = null;
						frameAnchorInvalidRef.current = true;
						await playerRef.current.setMode("recorded", {
							timeRange: {
								context: "custom",
								startTime: startTime.toISOString(),
								endTime: endTime.toISOString()
							}
						});
						playerRef.current.play();
						EventBus.publish("RecordedBottomBar_startVideo", { instanceId, playing: true });
					}
					break;
				case "setSpeed":
					playerRef.current.setSpeed(cmd.speed);
					break;
				case "stepForward": {
					if (frameAnchorInvalidRef.current || currentFrameRef.current === null) {
						await playerRef.current.stepFrameForward();
						const playerFrameAfterStep = playerRef.current.getCurrentFrame();
						currentFrameRef.current = playerFrameAfterStep ?? null;
						frameAnchorInvalidRef.current = false;
					} else {
						const targetFrameForward = currentFrameRef.current + 1;

						await playerRef.current.seekToFrame(targetFrameForward);
						currentFrameRef.current = targetFrameForward;
					}

					publishCurrentRecordedTimestamp();
					break;
				}
				case "stepBackward": {
					if (frameAnchorInvalidRef.current || currentFrameRef.current === null) {
						await playerRef.current.stepFrameBackward();
						const playerFrameAfterStep = playerRef.current.getCurrentFrame();
						currentFrameRef.current = playerFrameAfterStep ?? null;
						frameAnchorInvalidRef.current = false;
					} else {
						const targetFrameBackward = Math.max(0, currentFrameRef.current - 1);

						await playerRef.current.seekToFrame(targetFrameBackward);
						currentFrameRef.current = targetFrameBackward;
					}

					publishCurrentRecordedTimestamp();
					break;
				}
			}
		}
	};

	useEffect(() => {
		const previousMode = previousSelectedVideoModeRef.current;
		previousSelectedVideoModeRef.current = selectedVideoMode;

		// The mode prop initializes a newly mounted player. Only issue an imperative
		// mode command when the selected mode actually changes after mount.
		if (previousMode === selectedVideoMode) {
			return;
		}

		if (playerRef.current) {
			// setMode("live") explicit on selectedVideoMode change, update to mode prop not sufficient
			// recorded handled by event bus in execPlaybackCommand
			if (selectedVideoMode === "live") {
				setError(false);
				setErrorMessage(null);
				setLoading(true);
				if (needsFullReinitForLive.current) {
					// Player started in recorded mode: ILB/live stream was never initialized.
					// Force a full remount in live mode to trigger the stream setup.
					needsFullReinitForLive.current = false;
					setRemountKey(k => k + 1);
				} else {
					playerRef.current.setMode("live");
				}
			}
		}
	}, [selectedVideoMode]);

	const handleStatusChange = status => {
		switch (status) {
			case "playing":
				setLoading(false);
				setError(false);
				setErrorMessage(null);
				break;
			case "loading":
				setLoading(true);
				break;
			case "ended":
				if (selectedVideoModeRef.current === "recorded" && cursorRef.current.loop === true) {
					EventBus.publish("RecordedBottomBar_startVideo", { instanceId, playing: true });
					playerRef.current.seek(0);
					playerRef.current.play();
				}
			default:
				console.debug(`Player status changed to ${status} for ${camera.name}:`);
				break;
		}
	};

	const handleError = err => {
		const message = (err && (err.message || err.toString())) || "";
		const shouldSuppress = SUPPRESS_ERRORS.some(s => message.includes(s));

		if (!shouldSuppress) {
			setLoading(false);
			setError(true);
			cameraService.checkCertValid(
				selectedVideoModeRef.current === "live" ? camera.player?.mtxApiUrl : camera.player?.hlsUrl,
				(err, result) => {
					if (err) {
						console.error("Error validating stream certificate:", err);
					} else if (result?.isCertValid === false) {
						setErrorMessage(getTranslation("global.CBComponents.video.webRTCPlayer.certError"));
					}
				}
			);
		}
		console.error("Native Player Error:", err);
	};

	const handleTimeUpdate = time => {
		if (selectedVideoModeRef.current === "live") return;
		// time is seconds since start time
		const { startTime, endTime, duration } = cursorRef.current || {};
		if (!startTime || !endTime) return;
		const currentTimestamp = new Date(startTime.getTime() + time * 1000);
		EventBus.publish("setCurrentRecordedTimestamp", { instanceId, time: currentTimestamp });
	};

	const handleDurationChange = duration => {
		if (selectedVideoModeRef.current === "recorded") {
			cursorRef.current.loadedDuration = duration;
			EventBus.publish("loadedDurationChange", { instanceId, duration });
		}
	};

	const handlePlayerReady = () => {
		playerReadyRef.current = true;

		if (pendingLiveMode.current) {
			// reconnect() reinitializes to the original mode prop ("recorded"); switch back to live
			pendingLiveMode.current = false;
			playerRef.current?.setMode("live");
		} else if (selectedVideoModeRef.current !== "live") {
			// Publish player capabilities when ready (only in recorded mode)
			console.debug(
				`[VideoTranslatorPlayer][${instanceId}] Publishing capabilities (handlePlayerReady in recorded mode):`,
				STANDARD_SPEED_RANGE
			);
			EventBus.publish("VideoPlayer_CapabilitiesReady", {
				instanceId,
				supportedSpeeds: STANDARD_SPEED_RANGE
			});
		}

		if (pendingCommandsRef.current.length > 0) {
			pendingCommandsRef.current.forEach(cmd => execPlaybackCommand(cmd));
			pendingCommandsRef.current = [];
		}
	};

	const handleRetryStream = () => {
		setError(false);
		setErrorMessage(null);
		setLoading(true);
		if (selectedVideoModeRef.current === "live") {
			pendingLiveMode.current = true;
		}
		playerRef.current.reconnect();
	};

	return (
		<div style={{ position: "relative" }} ref={playerDiv}>
			{(camera.player?.backend !== "eclipse" && streamUrl) ||
			(camera.player?.backend === "eclipse" && eclipseStreamId) ? (
				<Player
					key={remountKey}
					ref={player => {
						playerRef.current = player;
					}}
					streamId={camera.player?.backend === "eclipse" ? eclipseStreamId : camera.id}
					storageServiceUrl={camera.player.eclipseApi}
					recordedProvider={camera.player.storageProvider} // default undefined which will use the default provider in VideoTranslator
					name={camera.entityData.properties.name || camera.id}
					rtspUrl={streamUrl}
					streamSetup={player.streamSetup || "auto"}
					ilbServiceUrl={player.ilbUrl}
					mediaMtxWebrtcUrl={player.mtxWebrtcUrl}
					mediaMtxApiUrl={player.mtxApiUrl}
					hlsServiceUrl={player.hlsUrl}
					storageManifestExtendLiveTolerance={player?.storageManifestExtendLiveTolerance ?? 2}
					hlsMinSegments={player?.hlsMinSegments ?? 1}
					hlsWaitTimeoutMs={player?.hlsWaitTimeoutMs ?? 10000}
					hlsWaitIntervalMs={player?.hlsWaitIntervalMs ?? 500}
					webrtcConfig={{ firstFrameTimeoutMs: player.firstFrameTimeoutMs ?? 5000 }}
					theme={"default"}
					mode={selectedVideoModeRef.current}
					autoplay={selectedVideoModeRef.current === "live" ? true : false}
					muted={true}
					controls={false}
					onReady={handlePlayerReady}
					onTimeUpdate={handleTimeUpdate}
					onStatusChange={handleStatusChange}
					onError={handleError}
					onDurationChange={handleDurationChange}
					hideTakeScreenshot={true}
					hideDownloadClip={true}
					hideStreamInfo={true}
					hideShowHideControls={true}
					hideReconnect={true}
					hideLoadingOverlay={true}
					hidePlayButton={true}
					hideErrorOverlay={true}
					roundedCorners={false}
					{...(authProviderRef.current ? { authProvider: authProviderRef.current } : {})} data-testid="orion-components-cbcomponents-video-web-rtcplayer-video-translator-player-player"
				/>
			) : null}
			{loading && !error && (
				<div
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: "rgba(0,0,0,0.3)",
						zIndex: 2
					}}
				>
					<CircularProgress data-testid="orion-components-cbcomponents-video-web-rtcplayer-video-translator-player-circular-progress" />
				</div>
			)}
			{error && (
				<div
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: "rgba(0,0,0,0.6)",
						zIndex: 1901
					}}
				>
					<StreamFailed retryStream={handleRetryStream} message={errorMessage} />
				</div>
			)}
		</div>
	);
};

VideoTranslatorPlayer.propTypes = {
	player: PropTypes.object.isRequired,
	camera: PropTypes.object.isRequired,
	streamUrl: PropTypes.string
};

export default VideoTranslatorPlayer;
